'use client';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Link, Typography, useTheme } from '@mui/material';
import Image from 'next/image';
import { usePathname } from 'next/navigation';

import { ArticleShareLinks } from '@/components/ResourceCenter/ArticleShareLinks';
import { getImageUrl } from '@/lib/helpers/getImageUrl';
import { getPageCodeFromPath } from '@/lib/helpers/pageHelpers';
import { buildUrlWithUtmParams } from '@/lib/helpers/utmParamHelpers';
import { ComponentSharedArticleSidebarCta } from '@/types';

interface ArticleSidebarCtaCardProps {
  shareTitle: string;
  sharePath: string;
  contentLabel: 'article' | 'page';
  cta: ComponentSharedArticleSidebarCta;
  fixedSidebar?: boolean;
}

export function ArticleSidebarCtaCard({
  shareTitle,
  sharePath,
  contentLabel,
  cta,
  fixedSidebar = false,
}: ArticleSidebarCtaCardProps) {
  const theme = useTheme();
  const pathname = usePathname();
  const phoneImageUrl = getImageUrl({ image: cta.PhoneImage, size: 'small' });
  const rawButtonUrl = cta.ButtonUrl?.trim();
  // Decorate the apply link with UTM params derived from the current page path
  // so leads trace back to the exact article (matches commercial-page CTA behaviour).
  const buttonHref = rawButtonUrl
    ? buildUrlWithUtmParams(rawButtonUrl, getPageCodeFromPath(pathname))
    : rawButtonUrl;

  return (
    <Box
      component="aside"
      aria-label={cta.Title || 'Article call to action'}
      sx={{
        borderRadius: 2,
        background: theme.palette.purple[100],
        p: fixedSidebar ? { xs: 2, md: 2.5 } : { xs: 3, md: 3.5 },
      }}
    >
      {cta.Title && (
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.brand.primarySecondary1,
            fontWeight: 700,
            mb: 3,
            textAlign: 'center',
          }}
        >
          {cta.Title}
        </Typography>
      )}

      {!!cta.Items?.length && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, mb: 3 }}>
          {cta.Items.filter(Boolean).map((item) => (
            <Box
              key={item!.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.25rem minmax(0, 1fr)',
                gap: 1.5,
                alignItems: 'start',
              }}
            >
              <CheckIcon
                sx={{ mt: 0.25, color: theme.palette.brand.primarySecondary1, fontSize: 18 }}
              />
              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                {item!.BoldText && <Box component="strong">{item!.BoldText}</Box>} {item!.Text}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {cta.ButtonText && buttonHref && (
        <Link
          href={buttonHref}
          underline="none"
          sx={{
            minHeight: 48,
            px: 2.5,
            mb: cta.PhoneNumber ? 2 : 0,
            borderRadius: 999,
            color: theme.palette.common.white,
            background: `linear-gradient(90deg, ${theme.palette.brand.secondary}, ${theme.palette.brand.secondary1})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            fontWeight: 700,
            fontSize: 14,
            '&:hover': {
              background: `linear-gradient(90deg, ${theme.palette.brand.primarySecondary3}, ${theme.palette.brand.secondary})`,
            },
          }}
        >
          {cta.ButtonText}
          <ArrowForwardIcon sx={{ fontSize: 20 }} />
        </Link>
      )}

      {cta.PhoneNumber && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
            color: theme.palette.text.primary,
            fontSize: 16,
          }}
        >
          {phoneImageUrl && (
            <Image
              src={phoneImageUrl}
              alt={cta.PhoneImage?.alternativeText || 'Contact'}
              width={24}
              height={24}
              style={{ borderRadius: '50%', objectFit: 'cover' }}
            />
          )}
          <Link
            href={`tel:${cta.PhoneNumber.replace(/[^\d+]/g, '')}`}
            underline="none"
            color="inherit"
          >
            {cta.PhoneNumber}
          </Link>
        </Box>
      )}

      {cta.ShowShareLinks !== false && (
        <ArticleShareLinks
          title={shareTitle}
          path={sharePath}
          contentLabel={contentLabel}
          enabledNetworks={{
            facebook: cta.ShareFacebook,
            instagram: cta.ShareInstagram,
            linkedIn: cta.ShareLinkedIn,
            mail: cta.ShareMail,
            x: cta.ShareX,
          }}
          showLabels={cta.ShowShareLabels !== false}
        />
      )}
    </Box>
  );
}
